import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { PartnerStatusProvider } from './context/PartnerStatusContext';
import { ChatProvider } from './context/ChatContext';
import ProtectedRoute from './components/Auth/ProtectedRoute';
import ManagerRoute from './components/Auth/ManagerRoute';
import FinanceRoute from './components/Auth/FinanceRoute';
import SidebarLayout from './components/Layout/SidebarLayout';
import Login from './components/Auth/Login';
import AuthCallback from './components/Auth/AuthCallback';
import Dashboard from './components/Dashboard/Dashboard';
import WorkPlan from './components/WorkPlan/WorkPlan';
import CloseWorkDay from './components/CloseWorkDay/CloseWorkDay';
import WorkPlanSummary from './components/WorkPlan/WorkPlanSummary';
import AddToWorkPlan from './components/WorkPlan/AddToWorkPlan';
import Projects from './components/Projects/Projects';
import ProjectDetails from './pages/ProjectDetails';
import ProjectDetailsV2 from './pages/ProjectDetailsV2';
import ProjectActivityHistory from './pages/ProjectActivityHistory';
import MyProjects from './pages/MyProjects';
import ProjectNotesArtifactsView from './pages/ProjectNotesArtifactsView';
import ReleaseManagement from './pages/ReleaseManagement';
import ReleaseDetail from './pages/ReleaseDetail';
import BirdsviewDashboard from './pages/BirdsviewDashboard';
import TheHub from './pages/TheHub';
import WorkPlanHistory from './pages/WorkPlanHistory';
import CompactWorkPlanHistory from './pages/CompactWorkPlanHistory';
import WorkPlanCalendar from './pages/WorkPlanCalendar';
import TimeOffs from './pages/TimeOffs';
import Scribble from './pages/Scribble';
import TimeCapsule from './pages/TimeCapsule';
import PartnerCompanies from './pages/PartnerCompanies';
import NexGate from './pages/NexGate';
import PeterConversations from './pages/PeterConversations';
import FinanceAttendance from './pages/FinanceAttendance';
import FinancePayslips from './pages/FinancePayslips';
import ChatPage from './bot/pages/ChatPage';
import FloatingChatWidget from './components/BotChat/FloatingChatWidget';
import ChatUrlHandler from './components/BotChat/ChatUrlHandler';
import HolidayCalendar from './pages/HolidayCalendar';
import LeaveManagement from './pages/LeaveManagement';
import DailyWorkPlanReview from './pages/DailyWorkPlanReview';
import ProjectReviewDetail from './pages/DailyWorkPlanReview/ProjectReviewDetail';
import UserAllocationReview from './pages/DailyWorkPlanReview/UserAllocationReview';
import ResourceReviewFlow from './pages/DailyWorkPlanReview/ResourceReviewFlow';
import TodayOutcome from './pages/DailyWorkPlanReview/TodayOutcome';
import TaskConversations from './pages/TaskConversations';
import SocialWall from './pages/SocialWall';
import FinancePayoutSummary from './pages/FinancePayoutSummary';
import FeatureDesignStudio from './pages/FeatureDesignStudio';
import HirelaneDashboard from './pages/HirelaneDashboard';
import HirelaneJobCreatePage from './pages/HirelaneJobCreatePage';
import HirelaneJobDetailPage from './pages/HirelaneJobDetailPage';
import HirelaneCandidateClarifyPage from './pages/HirelaneCandidateClarifyPage';

function RedirectProjectDetailsV2PathToDefault() {
  const { projectId } = useParams();
  return <Navigate to={`/projects/${projectId}`} replace />;
}

// Wrapper component for protected routes with sidebar
const ProtectedLayout = ({ children }) => (
  <ProtectedRoute>
    <SidebarLayout>
      {children}
    </SidebarLayout>
  </ProtectedRoute>
);

function App() {
  return (
    <AuthProvider>
      <PartnerStatusProvider>
        <ChatProvider>
          <Router>
            <ChatUrlHandler />
            <Routes>
              {/* Public Routes */}
              <Route path="/login" element={<Login />} />
              <Route path="/auth/callback" element={<AuthCallback />} />
              {/* Keep ChatPage route for backward compatibility, but ChatUrlHandler will intercept it */}
              <Route path="/chat/:conversationId" element={<ChatPage />} />
              <Route path="/hirelane/clarify" element={<HirelaneCandidateClarifyPage />} />

              {/* Protected Routes */}
              <Route
                path="/dashboard"
            element={
              <ProtectedLayout>
                <Dashboard />
              </ProtectedLayout>
            }
          />
          <Route
            path="/birdsview"
            element={
              <ProtectedLayout>
                <BirdsviewDashboard />
              </ProtectedLayout>
            }
          />
          <Route
            path="/projects"
            element={
              <ProtectedLayout>
                <ManagerRoute>
                <Projects />
                </ManagerRoute>
              </ProtectedLayout>
            }
          />
          <Route
            path="/thehub"
            element={
              <ProtectedLayout>
                <TheHub />
              </ProtectedLayout>
            }
          />
          <Route
            path="/work-plan-history/:userId"
            element={
              <ProtectedLayout>
                <WorkPlanHistory />
              </ProtectedLayout>
            }
          />
          <Route
            path="/compact-work-plan-history/:userId"
            element={
              <ProtectedLayout>
                <CompactWorkPlanHistory />
              </ProtectedLayout>
            }
          />
          <Route
            path="/projects/:projectId/v2"
            element={
              <ProtectedLayout>
                <ManagerRoute>
                  <RedirectProjectDetailsV2PathToDefault />
                </ManagerRoute>
              </ProtectedLayout>
            }
          />
          <Route
            path="/projects/:projectId/legacy"
            element={
              <ProtectedLayout>
                <ManagerRoute>
                <ProjectDetails />
                </ManagerRoute>
              </ProtectedLayout>
            }
          />
          <Route
            path="/projects/:projectId"
            element={
              <ProtectedLayout>
                <ManagerRoute>
                <ProjectDetailsV2 />
                </ManagerRoute>
              </ProtectedLayout>
            }
          />
          <Route
            path="/projects/:projectId/activity-history"
            element={
              <ProtectedLayout>
                <ManagerRoute>
                <ProjectActivityHistory />
                </ManagerRoute>
              </ProtectedLayout>
            }
          />
          <Route
            path="/my-projects"
            element={
              <ProtectedLayout>
                <MyProjects />
              </ProtectedLayout>
            }
          />
          <Route
            path="/my-projects/:projectId/notes-artifacts"
            element={
              <ProtectedLayout>
                <ProjectNotesArtifactsView />
              </ProtectedLayout>
            }
          />
          <Route
            path="/projects/:projectId/releases"
            element={
              <ProtectedLayout>
                <ReleaseManagement />
              </ProtectedLayout>
            }
          />
          <Route
            path="/projects/:projectId/releases/:releaseId"
            element={
              <ProtectedLayout>
                <ReleaseDetail />
              </ProtectedLayout>
            }
          />
          <Route
            path="/work-plan"
            element={
              <ProtectedLayout>
                <WorkPlan />
              </ProtectedLayout>
            }
          />
          <Route
            path="/close-work-day"
            element={
              <ProtectedLayout>
                <CloseWorkDay />
              </ProtectedLayout>
            }
          />
          <Route
            path="/close-work-day/:workPlanId"
            element={
              <ProtectedLayout>
                <CloseWorkDay />
              </ProtectedLayout>
            }
          />
          <Route
            path="/work-plan-summary"
            element={
              <ProtectedLayout>
                <WorkPlanSummary />
              </ProtectedLayout>
            }
          />
          <Route
            path="/add-to-work-plan"
            element={
              <ProtectedLayout>
                <AddToWorkPlan />
              </ProtectedLayout>
            }
          />
          <Route
            path="/work-plan-calendar/:userId"
            element={
              <ProtectedLayout>
                <WorkPlanCalendar />
              </ProtectedLayout>
            }
          />
          <Route
            path="/time-offs"
            element={
              <ProtectedLayout>
                <TimeOffs />
              </ProtectedLayout>
            }
          />
          <Route
            path="/scribble"
            element={
              <ProtectedLayout>
                <Scribble />
              </ProtectedLayout>
            }
          />
          <Route
            path="/time-capsule"
            element={
              <ProtectedLayout>
                <TimeCapsule />
              </ProtectedLayout>
            }
          />
          <Route
            path="/partner-companies"
            element={
              <ProtectedLayout>
                <PartnerCompanies />
              </ProtectedLayout>
            }
          />
          <Route
            path="/nexgate"
            element={
              <ProtectedLayout>
                <NexGate />
              </ProtectedLayout>
            }
          />
          <Route
            path="/feature-design-studio"
            element={
              <ProtectedLayout>
                <FeatureDesignStudio />
              </ProtectedLayout>
            }
          />
          <Route
            path="/hirelane"
            element={
              <ProtectedLayout>
                <HirelaneDashboard />
              </ProtectedLayout>
            }
          />
          <Route
            path="/hirelane/jobs/new"
            element={
              <ProtectedLayout>
                <HirelaneJobCreatePage />
              </ProtectedLayout>
            }
          />
          <Route
            path="/hirelane/jobs/:jobId/edit"
            element={
              <ProtectedLayout>
                <HirelaneJobCreatePage />
              </ProtectedLayout>
            }
          />
          <Route
            path="/hirelane/jobs/:jobId"
            element={
              <ProtectedLayout>
                <HirelaneJobDetailPage />
              </ProtectedLayout>
            }
          />
          <Route
            path="/peter-conversations"
            element={
              <ProtectedLayout>
                <PeterConversations />
              </ProtectedLayout>
            }
          />
          <Route
            path="/my-workplace/holiday-calendar"
            element={
              <ProtectedLayout>
                <HolidayCalendar />
              </ProtectedLayout>
            }
          />
          <Route
            path="/my-workplace/leave-management"
            element={
              <ProtectedLayout>
                <LeaveManagement />
              </ProtectedLayout>
            }
          />
          <Route
            path="/daily-work-plan-review"
            element={
              <ProtectedLayout>
                <DailyWorkPlanReview />
              </ProtectedLayout>
            }
          />
          <Route
            path="/daily-work-plan-review/project/:projectId"
            element={
              <ProtectedLayout>
                <ProjectReviewDetail />
              </ProtectedLayout>
            }
          />
          <Route
            path="/daily-work-plan-review/project/:projectId/user-allocation"
            element={
              <ProtectedLayout>
                <UserAllocationReview />
              </ProtectedLayout>
            }
          />
          <Route
            path="/daily-work-plan-review/project/:projectId/resource-review"
            element={
              <ProtectedLayout>
                <ResourceReviewFlow />
              </ProtectedLayout>
            }
          />
          <Route
            path="/daily-work-plan-review/project/:projectId/today-outcome"
            element={
              <ProtectedLayout>
                <TodayOutcome />
              </ProtectedLayout>
            }
          />
          <Route
            path="/task-conversations"
            element={
              <ProtectedLayout>
                <TaskConversations />
              </ProtectedLayout>
            }
          />
          <Route
            path="/social-wall"
            element={
              <ProtectedLayout>
                <SocialWall />
              </ProtectedLayout>
            }
          />

          {/* Finance Routes */}
          <Route
            path="/finance-attendance"
            element={
              <ProtectedLayout>
                <FinanceRoute>
                  <FinanceAttendance />
                </FinanceRoute>
              </ProtectedLayout>
            }
          />
          <Route
            path="/finance-payslips"
            element={
              <ProtectedLayout>
                <FinanceRoute>
                  <FinancePayslips />
                </FinanceRoute>
              </ProtectedLayout>
            }
          />
          <Route
            path="/finance-payout-summary"
            element={
              <ProtectedLayout>
                <FinanceRoute>
                  <FinancePayoutSummary />
                </FinanceRoute>
              </ProtectedLayout>
            }
          />

          {/* Redirect root to dashboard */}
          <Route
            path="/"
            element={<Navigate to="/dashboard" replace />}
          />

          {/* Catch all other routes and redirect to dashboard */}
          <Route
            path="*"
            element={<Navigate to="/dashboard" replace />}
          />
        </Routes>
        {/* Floating Chat Widget - Available on all pages */}
        {/* <FloatingChatWidget /> */}
      </Router>
        </ChatProvider>
      </PartnerStatusProvider>
    </AuthProvider>
  );
}

export default App; 